import { zodResolver } from '@hookform/resolvers/zod';
import type { CreateInvoiceRequestPayload,
  CreateInvoiceResponse } from '@weft/api-definition';
import { CreateInvoiceResponseSchema } from '@weft/api-definition';
import { isNotNullOrUndefined } from '@weft/utils';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useFormPersist from 'react-hook-form-persist';
import type { UseMutationOptions } from 'react-query';
import { useMutation } from 'react-query';
import { ErrorLabel } from '../../../../../components/design/ErrorLabel';
import { LocalStorageKeys } from '../../../../../constants/local-storage-keys';

import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { getPrivateApi } from '../../../../../services/api';
import { runMutation } from '../../../../../utils/vy-mutation';
import { CREATE_INVOICE_DEFAULT_VALUES } from './create-invoice-form-consts/create-invoice-default-values';
import type { InvoiceFormInput } from './create-invoice-form-types/invoice-form-input';
import type { NewInvoiceForm } from './create-invoice-form-types/new-invoice-form';
import { NewInvoiceFormSchema } from './create-invoice-form-types/new-invoice-form';

export function useCreateInvoice(options?: UseMutationOptions<CreateInvoiceResponse, unknown, CreateInvoiceRequestPayload>) {
  return useMutation(async (newInvoice) => {
    const api = getPrivateApi();
    const createInvoiceResponse = await api.createInvoice({ createInvoiceRequest: newInvoice });

    return CreateInvoiceResponseSchema.parse(createInvoiceResponse);
  }, options);
}

export function withCreateInvoiceForm(Component: React.FC) {
  return () => {
    const navigate = useVyNavigate();

    const createInvoiceMutation = useCreateInvoice();

    const methods = useForm<InvoiceFormInput, NewInvoiceForm>({
      resolver: zodResolver(NewInvoiceFormSchema),
      defaultValues: CREATE_INVOICE_DEFAULT_VALUES,
    });

    useFormPersist(LocalStorageKeys.CreateInvoice, {
      watch: methods.watch,
      setValue: methods.setValue,
      storage: window.localStorage,
    });

    const onSubmit = methods.handleSubmit(async (data) => {
      const validatedInvoiceInput = NewInvoiceFormSchema.parse(data);

      if (isNotNullOrUndefined(validatedInvoiceInput)) {
        await runMutation(
          createInvoiceMutation,
          validatedInvoiceInput,
          {
            pending: 'Saving...',
            success: 'Saved',
            error: 'Failed to save invoice',
          },
          {
            onSuccess: async ({ invoice: savedInvoice }) => {
              localStorage.removeItem(LocalStorageKeys.CreateInvoice);
              navigate(`/invoices/overview/dynamic/${savedInvoice.id}`);
            },
          },
        );
      }
    });

    const errorRoot = methods.formState.errors.root;

    return (
      <FormProvider {...methods}>
        <form
          onSubmit={onSubmit}
          className="flex h-full flex-col"
          noValidate
        >
          <Component/>
        </form>

        {isNotNullOrUndefined(errorRoot) &&
          (<ErrorLabel errorMessage={errorRoot?.message ?? ''}/>)
        }
      </FormProvider>
    );
  };
}